// The applicant's own page: /apply/<token>, from the link an agent's referral emails them. No sign-in — the token is
// the key. Four steps, shown like the website's application form ("Step N of 4"): the done ones as one ticked line each,
// the current one (the first the server's list has not done) in full, the rest greyed with what they wait on. Holding
// deposit; referencing (Goodlord's — "Continue your referencing" opens their form, the status, a guarantor when they ask
// for one, and an upload box only for a required ID document not yet on file); sign the agreement (read on the page and
// signed in the tenant's box on its last page); first month's rent and deposit. Each ticks the reservation sheet by itself.
// Payments: until a card gateway is connected (REQUIRES EXTERNAL CREDENTIAL) the tenant pays by bank transfer and
// presses "I've paid"; Lotus Living confirms. When the agent or Lotus Living emailed a payment link for the holding deposit
// (docs/plans/holding-deposit-payment-link.md), "Pay the holding deposit" opens it, with "I've paid" kept underneath.
// Lotus Living's "Ask to refill" shows as a banner with Update my application (the website's form, pre-filled — RefillBlock),
// and My application shows what they sent, read-only, under the steps (docs/plans/lettings-one-form.md, phase 4).
import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Lock, Clock, FileText, Trash2, Upload as UploadIcon } from 'lucide-react';
import { SiteLayout } from '../../components/site/SiteLayout';
import { AgreementViewer, SignatureBox } from '../../components/site/AgreementViewer';
import { PillButton, PillLink } from '../../components/site/Pill';
import { applicantApi, fileToUpload, bedsLabel, floorLabel, type ApplicantView, type ApplicantStep } from '../../lib/reservations';
import { SiteApplicationForm, SiteApplicationSummary, siteFormErrors } from '../../components/site/ApplicationForm';
import { fromApplication, splitName, toPayload, type ApplicationState } from '../../lib/applicationForm';
import { formatDate, formatMoney } from '../../lib/viz';
import { PhoneInput } from '../../components/ui/PhoneInput';
import { phoneOk, phoneError } from '../../lib/phone';
import { formatPhone } from '../../lib/phone';

const money = (n: number, c: string) => formatMoney(n, c).replace(/\.00$/, '');
// Phones only: the link opens the Lotus Living app when it is installed (lotusliving://, the app's scheme).
const onPhone = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const r = (i: number) => ({ '--i': i }) as CSSProperties;
/** The note at the foot of the page: the link is the key, so it stays with the applicant. */
const linkNote = (until: string | null | undefined) => `Personal link${until ? `, valid until ${formatDate(until)}` : ''} — please don't forward it.`;

export default function ApplicantPage() {
  const { token = '' } = useParams<{ token: string }>();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['applicant', token], queryFn: () => applicantApi.view(token), enabled: !!token, retry: false });
  const v = q.data;
  const put = (d: ApplicantView) => qc.setQueryData(['applicant', token], d);
  if (q.isLoading) return <SiteLayout chat={false}><div className="site-narrow py-32 text-center text-[14px] text-ink-400">Opening your application…</div></SiteLayout>;
  if (!v) {
    const err = q.error as { status?: number; message?: string } | null;
    return (
      <SiteLayout chat={false}>
        <div className="site-narrow py-20 sm:py-28">
          <div className="mx-auto max-w-xl text-center">
            <Lock className="mx-auto h-10 w-10 text-ink-300" />
            <h1 className="h-section mt-6 text-ink-900">{err?.status === 410 ? 'This link has expired' : 'This link does not open an application'}</h1>
            <p className="mt-4 text-[15px] leading-relaxed text-ink-500">{err?.status === 410 ? 'Ask your agent or Lotus Living for a fresh link — nothing you did is lost.' : 'Check the link in your email, or ask your agent to send it again.'}</p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const handed = v.status !== 'applying';
  const first = v.applicant.name.split(/\s+/)[0] || 'there';
  // The review gate: until Lotus Living approves, only ID and right to rent; a rejection says why.
  if (v.reviewStatus !== 'APPROVED') return <ReviewScreen token={token} v={v} first={first} onDone={put} />;
  return (
    <SiteLayout chat={false}>
      <div className="site-narrow pb-20 pt-8 lg:pb-32 lg:pt-12">
        <div className="grid gap-10 lg:grid-cols-5 lg:gap-16">
          <div className="min-w-0 lg:col-span-3">
            <p className="rise eyebrow" style={r(0)}>Your application</p>
            <h1 className="rise h-section mt-3 text-ink-900" style={r(1)}>Flat {v.flat} at {v.property}</h1>
            <p className="rise mt-3 text-[15px] leading-relaxed text-ink-500" style={r(2)}>
              {handed
                ? <>Thank you, {first} — everything is in{v.tenancy ? <> (tenancy <b className="font-medium text-ink-800">{v.tenancy.reference}</b>)</> : ''}. We'll email you to set up your account and arrange your keys.</>
                : <>Hello {first}. Four steps to your keys — here's the next one.</>}
            </p>
            {onPhone && <a href={`lotusliving://apply/${encodeURIComponent(token)}`} className="mt-1 inline-flex min-h-[44px] items-center text-[14px] font-medium text-bronze-600 underline underline-offset-4">Open in the Lotus Living app</a>}
            {!handed && <RefillBlock token={token} v={v} onDone={put} />}

            <Steps token={token} v={v} onDone={put} readOnly={handed} />
            {v.application && <div className="mt-6 sm:mt-8"><SiteApplicationSummary form={v.application} /></div>}
          </div>

          <aside className="lg:col-span-2">
            <div className="surface p-5 sm:p-6 lg:sticky lg:top-28">
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-ink-400">The home</p>
              <p className="h-card mt-1.5 text-ink-900">Flat {v.flat}</p>
              <p className="mt-1 text-[14px] text-ink-600">{v.property} · {floorLabel(v.floor)}</p>
              <dl className="mt-5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-3 border-t border-ink-100 pt-5 text-[13.5px]">
                <Row k="Type" val={`${bedsLabel(v.bedrooms, v.unitType)}${v.areaSqft ? ` · ${v.areaSqft} sq ft` : ''}`} />
                <Row k="Rent" val={`${money(v.rentMinor, v.currency)} a month`} />
                <Row k="Moving in" val={v.startDate ? formatDate(v.startDate) : 'To be agreed'} />
                <Row k="Term" val={v.term} />
                <Row k="Holding deposit" val={`${money(v.holdingDeposit.amountMinor, v.currency)} (one week)`} />
                <Row k="Deposit" val={`${money(v.money.depositMinor, v.currency)} (${v.money.depositWeeks} weeks)`} />
                {(v.parking || v.furniture) && <Row k="Asked for" val={[v.parking && 'Parking', v.furniture && 'Furniture'].filter(Boolean).join(', ')} />}
              </dl>
              {v.agent && (
                <div className="mt-5 border-t border-ink-100 pt-5 text-[13px] text-ink-600">
                  <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-ink-400">Your agent</p>
                  <p className="mt-1.5 font-medium text-ink-800">{v.agent.name}</p>
                  {v.agent.phone && <p className="mt-0.5 select-all">{formatPhone(v.agent.phone)}</p>}
                  {v.agent.email && <p className="mt-0.5 select-all">{v.agent.email}</p>}
                </div>
              )}
              <p className="mt-5 border-t border-ink-100 pt-4 text-[12px] leading-relaxed text-ink-400">{linkNote(v.linkExpiresAt)}</p>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}

/**
 * Before approval (SUBMITTED, or a legacy QUALIFIED): "Under review by Lotus Living" — the ID and right-to-rent boxes stay usable
 * (Lotus Living needs both to approve), nothing else shows. REJECTED: not successful, with the reason, read-only.
 */
/**
 * "Lotus Living asked you to update: …" (Ask to refill, docs/plans/lettings-one-form.md) with **Update my application**:
 * the website's form (components/site/ApplicationForm.tsx, mode apply) pre-filled with what they sent, where only what
 * Lotus Living asked for is required. Saving (POST /public/applicant/:token/form) replaces the application and clears the
 * request; "Thanks — Lotus Living has your update" stays until the page is left. The consents given with the application
 * are kept as they were unless Lotus Living asked for them again.
 */
function RefillBlock({ token, v, onDone }: { token: string; v: ApplicantView; onDone: (d: ApplicantView) => void }) {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  if (saved && !v.refill) {
    return (
      <div className="mt-6 flex items-start gap-3 rounded-xl border border-bronze-200 bg-bronze-50/60 p-4 sm:mt-8 sm:p-5">
        <Check className="mt-0.5 h-5 w-5 shrink-0 text-bronze-600" />
        <p className="text-[15px] font-medium text-ink-900">Thanks — Lotus Living has your update.</p>
      </div>
    );
  }
  if (!v.refill) return null;
  return (
    <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/70 p-4 sm:mt-8 sm:p-5">
      <p className="text-[15px] font-medium text-ink-900">Lotus Living asked you to update: {v.refill.labels.join(', ').toLowerCase()}</p>
      {v.refill.note && <p className="mt-1.5 text-[14px] leading-relaxed text-ink-700">{v.refill.note}</p>}
      <p className="mt-2 text-[13px] leading-relaxed text-ink-500">Asked {formatDate(v.refill.askedAt)} by {v.refill.askedBy}.</p>
      {!editing && <PillButton type="button" small onClick={() => setEditing(true)} className="mt-4 w-full sm:w-auto">Update my application</PillButton>}
      {editing && <RefillForm token={token} v={v} onCancel={() => setEditing(false)} onSaved={(d) => { setSaved(true); setEditing(false); onDone(d); }} />}
    </div>
  );
}

function RefillForm({ token, v, onCancel, onSaved }: { token: string; v: ApplicantView; onCancel: () => void; onSaved: (d: ApplicantView) => void }) {
  const asked = v.refill?.fields ?? [];
  const advert = v.rentRequest?.advertisedRentMinor ?? v.rentMinor;
  const [s, setS] = useState<ApplicationState>(() => fromApplication(v.application, { ...splitName(v.applicant.name), email: v.applicant.email ?? '', mobile: v.applicant.phone ?? '' }));
  const [tried, setTried] = useState(false);
  const set = (patch: Partial<ApplicationState>) => setS((x) => ({ ...x, ...patch }));
  const errs = siteFormErrors(s, advert, { asked });
  const save = useMutation({
    mutationFn: () => {
      const form = toPayload(s, { staff: true });
      const was = v.application?.consent;
      // The consents stand as given with the application, unless they were asked for again.
      if (was && typeof was === 'object' && !asked.includes('consent')) Object.assign(form, { consent: was });
      return applicantApi.form(token, form);
    },
    onSuccess: onSaved,
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (Object.keys(errs).length) { (e.currentTarget as HTMLElement).querySelector('[data-invalid]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
    save.mutate();
  };
  return (
    <form onSubmit={onSubmit} noValidate className="mt-4 rounded-[14px] border border-ink-200 bg-white p-4 sm:p-6">
      <p className="text-[16px] font-medium text-ink-900">Update my application</p>
      <p className="mt-1 text-[13.5px] leading-relaxed text-ink-500">Everything you sent is here — change what Lotus Living asked for (marked *) and anything else that has changed.</p>
      <SiteApplicationForm s={s} set={set} advert={advert} asked={asked} tried={tried} />
      <div className="mt-6 space-y-3">
        {save.isError && <p className="site-error">{(save.error as Error).message}</p>}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <PillButton type="submit" disabled={save.isPending} className="w-full sm:w-auto">{save.isPending ? 'Sending…' : 'Send my update'}</PillButton>
          <button type="button" onClick={onCancel} className="min-h-[44px] text-[14px] text-ink-500 underline underline-offset-4">Cancel</button>
        </div>
      </div>
    </form>
  );
}

function ReviewScreen({ token, v, first, onDone }: { token: string; v: ApplicantView; first: string; onDone: (d: ApplicantView) => void }) {
  const rejected = v.reviewStatus === 'REJECTED';
  const has = (c: string) => v.documents.some((d) => d.category === c);
  return (
    <SiteLayout chat={false}>
      <div className="site-narrow pb-20 pt-8 lg:pb-32 lg:pt-12">
        <div className="mx-auto max-w-2xl">
          <p className="rise eyebrow" style={r(0)}>Your application</p>
          <h1 className="rise h-section mt-3 text-ink-900" style={r(1)}>Flat {v.flat} at {v.property}</h1>
          {!rejected && <RefillBlock token={token} v={v} onDone={onDone} />}
          {rejected ? (
            <div className="rise mt-6 rounded-xl border border-ink-200 bg-white p-5 sm:mt-8 sm:p-6" style={r(2)}>
              <p className="text-[18px] font-medium text-ink-900">Your application was not successful</p>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-600">Sorry, {first} — Lotus Living can't go ahead with your application for this home.</p>
              {v.rejectionReason && <p className="mt-4 rounded-xl bg-ink-50 px-4 py-3 text-[14px] leading-relaxed text-ink-700"><span className="font-medium text-ink-900">Reason: </span>{v.rejectionReason}</p>}
              <p className="mt-4 text-[13.5px] leading-relaxed text-ink-500">Questions, or want another home? Reply to our email{v.agent ? <> or speak to {v.agent.name}</> : ''}.</p>
            </div>
          ) : (
            <>
              <div className="rise mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4 sm:mt-8 sm:p-5" style={r(2)}>
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                <div>
                  <p className="text-[16px] font-medium text-ink-900 sm:text-[17px]">Under review</p>
                  <p className="mt-1 text-[14px] leading-relaxed text-ink-600">Hello {first}. We'll email you once it's approved — the next steps then open here.</p>
                </div>
              </div>
              <RentOffer token={token} v={v} onDone={onDone} />
              <div className="rise mt-8" style={r(3)}>
                <p className="text-[16px] font-medium text-ink-900">Upload your documents</p>
                <p className="mt-0.5 text-[13.5px] text-ink-500">{rtrNeeded(v)
                  ? (has('id') && has('rtr') ? 'Both are in — thank you.' : 'Both are needed for the review.')
                  : (has('id') ? 'Your passport is in — thank you.' : 'Your passport is needed for the review.')}</p>
                <div className="mt-4 space-y-3">
                  <DocUploads token={token} v={v} onDone={onDone} readOnly={false} />
                </div>
              </div>
              {v.application && <div className="mt-8"><SiteApplicationSummary form={v.application} /></div>}
            </>
          )}
          <p className="mt-8 text-[12px] leading-relaxed text-ink-400">{linkNote(v.linkExpiresAt)}</p>
        </div>
      </div>
    </SiteLayout>
  );
}

/**
 * The rent the applicant offered below the advert: with Lotus Living (PENDING), or Lotus Living's counter-offer to accept
 * or decline (COUNTERED — the one thing that can be done under review; declining withdraws the application), or the answer.
 */
function RentOffer({ token, v, onDone }: { token: string; v: ApplicantView; onDone: (d: ApplicantView) => void }) {
  const qc = useQueryClient();
  const [declining, setDeclining] = useState(false);
  const m = useMutation({ mutationFn: (accept: boolean) => applicantApi.answerCounter(token, accept), onSuccess: (d) => { onDone(d); void qc.invalidateQueries({ queryKey: ['applicant', token] }); } });
  const r = v.rentRequest;
  if (!r) return null;
  const c = v.currency;
  if (r.status === 'COUNTERED') return (
    <div className="mt-4 rounded-xl border border-bronze-200 bg-white p-4 sm:p-5">
      <p className="text-[16px] font-medium text-ink-900 sm:text-[17px]">We can offer {money(r.counterMinor ?? 0, c)} a month</p>
      <p className="mt-1 text-[14px] leading-relaxed text-ink-600">You offered {money(r.requestedRentMinor ?? 0, c)}; advertised at {money(r.advertisedRentMinor ?? 0, c)}.{r.note ? ` ${r.note}` : ''} Accepting fixes it for the whole tenancy; declining withdraws your application.</p>
      <Err e={m.error} />
      {declining
        ? <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3"><span className="text-[14px] text-ink-700">Withdraw your application?</span><PillButton type="button" small variant="ghost" disabled={m.isPending} onClick={() => m.mutate(false)} className="w-full sm:w-auto">Yes, withdraw</PillButton><button type="button" className="min-h-[44px] text-[14px] text-ink-500 underline" onClick={() => setDeclining(false)}>Keep it</button></div>
        : <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3"><PillButton type="button" small disabled={m.isPending} onClick={() => m.mutate(true)} className="w-full sm:w-auto">{m.isPending ? 'Saving…' : `Accept ${money(r.counterMinor ?? 0, c)}`}</PillButton><button type="button" className="min-h-[44px] text-[14px] text-ink-500 underline" onClick={() => setDeclining(true)}>Decline</button></div>}
    </div>
  );
  const line = r.status === 'PENDING' ? `You offered ${money(r.requestedRentMinor ?? 0, c)} a month (advertised ${money(r.advertisedRentMinor ?? 0, c)}). We'll let you know.`
    : r.status === 'APPROVED' ? `Your rent is agreed at ${money(v.rentMinor, c)} a month.`
      : `Lotus Living cannot lower the rent; it stays at ${money(v.rentMinor, c)} a month.`;
  return <p className="mt-4 rounded-xl bg-ink-50 px-4 py-3 text-[14px] leading-relaxed text-ink-700">{line}</p>;
}

const Row = ({ k, val }: { k: string; val: string }) => <><dt className="text-ink-400">{k}</dt><dd className="min-w-0 text-right font-medium text-ink-800">{val}</dd></>;

/**
 * The four steps, as the website's form shows its own: "Step N of 4", the done steps above as one ticked line each, the
 * current step's content in full, the rest greyed below with what they wait on. The current step is the first one the
 * server's list has not done (it orders and locks them); once handed over nothing is current and the page is read-only.
 */
function Steps({ token, v, onDone, readOnly }: PartProps) {
  const idx = readOnly ? -1 : v.steps.findIndex((s) => !s.done);
  const current = idx >= 0 ? v.steps[idx] : undefined;
  const doneSteps = v.steps.filter((s) => s.done);
  const p = { token, v, onDone, readOnly };
  return (
    <div className="rise mt-6 sm:mt-8" style={r(3)}>
      <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-ink-500" aria-live="polite">
        {current ? `Step ${idx + 1} of ${v.total}` : doneSteps.length === v.total ? `All ${v.total} steps done` : `${v.done} of ${v.total} steps done`}
      </p>
      {doneSteps.length > 0 && (
        <ul className="mt-4 space-y-2">
          {doneSteps.map((s) => (
            <li key={s.key} className="flex items-center gap-3 text-[14px]">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-500 text-white"><Check className="h-3.5 w-3.5" /></span>
              <span className="min-w-0 text-ink-700">{doneLine(s, v)}</span>
            </li>
          ))}
        </ul>
      )}
      {current && (
        <section className="mt-5 rounded-xl border border-ink-200 bg-white p-4 shadow-card sm:p-6" aria-label={current.label}>
          <h2 className="h-card text-ink-900">{current.label}</h2>
          {current.pending && current.key === 'sign' && <p className="mt-1.5 inline-flex items-center gap-1.5 text-[13px] text-amber-700"><Clock className="h-3.5 w-3.5" />{current.pending}</p>}
          <div className="mt-4">
            {current.key === 'holding' && <Holding {...p} />}
            {current.key === 'referencing' && <Referencing {...p} step={current} />}
            {current.key === 'sign' && <Sign {...p} step={current} />}
            {current.key === 'money' && <Money {...p} step={current} />}
          </div>
        </section>
      )}
      {readOnly && <p className="mt-4 text-[13.5px] text-ink-500">Nothing more to do here.</p>}
    </div>
  );
}

/** One line for a done step: what was done and when. */
function doneLine(s: ApplicantStep, v: ApplicantView): string {
  const on = (d: string | null | undefined) => (d ? ` · ${formatDate(d)}` : '');
  switch (s.key) {
    case 'holding': return `Holding deposit paid${on(s.doneAt ?? v.holdingDeposit.declaredAt)}`;
    case 'referencing': return `Referencing passed${on(s.doneAt ?? v.referencing.approvedAt)}`;
    case 'sign': return `Tenancy agreement signed${on(s.doneAt ?? v.agreement.signedAt)}`;
    case 'money': return `First month's rent and deposit paid${on(s.doneAt ?? v.money.declaredAt)}`;
  }
}

type PartProps = { token: string; v: ApplicantView; onDone: (d: ApplicantView) => void; readOnly: boolean };
/** A labelled site control; `error` marks the box and shows under it. */
const F = ({ label, children, error, className = '' }: { label: string; children: ReactNode; error?: string; className?: string }) => (
  <label className={`site-field ${className}`} data-invalid={error ? '' : undefined}><span className="site-label">{label}</span>{children}{error && <span className="site-error">{error}</span>}</label>
);
const Err = ({ e }: { e: unknown }) => (e ? <p className="site-error">{(e as Error).message}</p> : null);
/** Primary action inside a step: full width on a phone. */
const ACTION = 'w-full disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto';
const ID_HINT = 'The photo page';
const RTR_HINT = 'Your share-code check result, BRP or visa page';
/** British and Irish need only the passport; anyone else a right to rent document too (server: documentsNeeded). */
const rtrNeeded = (v: ApplicantView) => (v.documentsNeeded ?? ['id', 'rtr']).includes('rtr');
/** The documents this applicant needs: the passport, and the right to rent document unless British or Irish (shown too when one is already on file). */
function DocUploads(p: PartProps) {
  const showRtr = rtrNeeded(p.v) || p.v.documents.some((d) => d.category === 'rtr');
  return (
    <>
      <Uploads {...p} category="id" label="Passport *" hint={ID_HINT} />
      {showRtr && <Uploads {...p} category="rtr" label={rtrNeeded(p.v) ? 'Right to rent document *' : 'Right to rent document'} hint={RTR_HINT} />}
    </>
  );
}

function PayBox({ lines, total, reference, currency, linked = false }: { lines: Array<[string, number]>; total: number; reference: string; currency: string; linked?: boolean }) {
  return (
    <div className="text-[14px]">
      <dl className="divide-y divide-ink-100 rounded-[14px] border border-ink-200 bg-white px-4">
        {lines.map(([k, n]) => <div key={k} className="flex justify-between gap-4 py-2.5"><dt className="text-ink-500">{k}</dt><dd className="shrink-0 font-medium tabular-nums text-ink-800">{money(n, currency)}</dd></div>)}
        {lines.length > 1 && <div className="flex justify-between gap-4 py-2.5"><dt className="font-medium text-ink-800">Total</dt><dd className="shrink-0 font-semibold tabular-nums text-ink-900">{money(total, currency)}</dd></div>}
      </dl>
      <p className="mt-3 text-[13.5px] leading-relaxed text-ink-600">{linked ? 'Pay with the link we emailed you, or by bank transfer to the account in your email' : 'Pay by bank transfer to the account in your email'}, reference <b className="select-all font-medium text-ink-900">{reference}</b>, then tap {linked ? 'I\'ve paid' : 'the button below'}.</p>
    </div>
  );
}

function Holding({ token, v, onDone }: PartProps) {
  const m = useMutation({ mutationFn: () => applicantApi.declare(token, 'holding'), onSuccess: onDone });
  const h = v.holdingDeposit;
  return (
    <div className="space-y-4">
      <PayBox lines={[['Holding deposit (one week)', h.amountMinor]]} total={h.amountMinor} reference={`${v.flat} ${v.applicant.name.split(/\s+/).slice(-1)[0] ?? ''}`.trim()} currency={v.currency} linked={!!h.paymentLink && !h.confirmed} />
      {h.declaredAt && !h.confirmed
        ? <p className="flex items-start gap-2 text-[14px] text-amber-700"><Clock className="mt-0.5 h-4 w-4 shrink-0" />Marked paid on {formatDate(h.declaredAt)} — we're checking the bank.</p>
        : h.confirmed ? <p className="flex items-center gap-2 text-[14px] text-emerald-700"><Check className="h-4 w-4" />Received, thank you.</p>
          : <>
            <Err e={m.error} />
            {/* The payment link the agent or Lotus Living emailed (docs/plans/holding-deposit-payment-link.md): paid there, then "I've paid". */}
            {h.paymentLink && <div><PillLink to={h.paymentLink} external small className={ACTION}>Pay the holding deposit</PillLink></div>}
            <div><PillButton type="button" small variant={h.paymentLink ? 'ghost' : 'dark'} disabled={m.isPending} onClick={() => m.mutate()} className={ACTION}>{m.isPending ? 'Saving…' : 'I\'ve paid the holding deposit'}</PillButton></div>
          </>}
    </div>
  );
}

function Uploads({ token, v, category, label, hint, onDone, accept = 'application/pdf,image/*' }: PartProps & { category: string; label: string; hint?: string; accept?: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const docs = v.documents.filter((d) => d.category === category);
  const up = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true); setError(null);
    try { onDone(await applicantApi.upload(token, { category, ...(await fileToUpload(file)) })); } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const del = async (id: string) => { setError(null); try { onDone(await applicantApi.remove(token, id)); } catch (e) { setError(e); } };
  const [over, setOver] = useState(false);
  const done = docs.length > 0;
  return (
    <div className={`rounded-xl border p-3 transition-colors sm:p-4 ${done ? 'border-emerald-200 bg-emerald-50/40' : 'border-ink-200 bg-white'}`}>
      <div className="flex items-center gap-2 px-1">
        <span className="min-w-0 flex-1 text-[14px] font-medium text-ink-900">{label}</span>
        {done && <Check className="h-4 w-4 shrink-0 text-emerald-600" aria-label="Uploaded" />}
      </div>
      {/* The dropzone: tap to pick a file (camera or files on a phone), or drop one on it. */}
      <label
        className={`mt-2.5 flex min-h-[64px] cursor-pointer items-center gap-3 rounded-xl border border-dashed px-4 py-3 transition ${over ? 'border-bronze-500 bg-bronze-50' : done ? 'border-emerald-300/80 bg-white/70 hover:border-emerald-400' : 'border-ink-300 bg-ink-50/50 hover:border-bronze-400 hover:bg-bronze-50/40'} ${busy ? 'pointer-events-none opacity-60' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); void up(e.dataTransfer.files?.[0]); }}
      >
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-bronze-600 shadow-sm ring-1 ring-ink-100"><UploadIcon className="h-4 w-4" /></span>
        <span className="min-w-0 leading-snug">
          <span className="block text-[14px] font-medium text-ink-800">{busy ? 'Uploading…' : done ? 'Add another file' : 'Upload a photo or PDF'}</span>
          {hint && <span className="block text-[12.5px] text-ink-500">{hint}</span>}
        </span>
        <input type="file" accept={accept} className="sr-only" disabled={busy} onChange={(e) => { void up(e.target.files?.[0]); e.target.value = ''; }} />
      </label>
      {docs.map((d) => (
        <div key={d.id} className="mt-1 flex min-h-[44px] items-center gap-2 px-1 text-[13.5px]">
          <FileText className="h-4 w-4 shrink-0 text-ink-400" /><a href={d.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-bronze-700 hover:underline">{d.name}</a>
          <span className="shrink-0 text-[12px] text-ink-400">{formatDate(d.uploadedAt)}</span>
          {d.mine && <button type="button" onClick={() => void del(d.id)} className="-mr-2 grid h-11 w-11 shrink-0 place-items-center rounded-full text-ink-400 hover:bg-red-50 hover:text-red-600" aria-label={`Remove ${d.name}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
      <div className="px-1"><Err e={error} /></div>
    </div>
  );
}

/** The status line for each of Goodlord's states. */
const REF_STATUS: Record<ApplicantView['referencing']['status'], { tone: 'wait' | 'ok' | 'warn' | 'bad'; text: string }> = {
  not_started: { tone: 'wait', text: 'Goes to Goodlord, our referencing partner, as soon as your holding deposit lands' },
  form_sent: { tone: 'wait', text: 'In progress' },
  in_review: { tone: 'wait', text: 'Goodlord is reviewing' },
  passed: { tone: 'ok', text: 'Passed' },
  guarantor_needed: { tone: 'warn', text: 'Passed — a guarantor is needed' },
  guarantor_in_review: { tone: 'wait', text: 'Goodlord is reviewing your guarantor' },
  failed: { tone: 'bad', text: 'Needs something more — Lotus Living will be in touch' },
};
const TONE = { wait: 'text-amber-700', ok: 'text-emerald-700', warn: 'text-amber-800', bad: 'text-red-700' };
const DOC_NAME: Record<string, string> = { id: 'Passport', rtr: 'Right to rent' };

function Referencing(p: PartProps & { step: ApplicantStep }) {
  const { token, v } = p;
  // Referencing is Goodlord's: "Continue your referencing" opens their own form (the link they emailed too); what stays
  // here is the status, a guarantor when Goodlord asks for one, and the ID documents — our legal duty, kept with us.
  const ref = v.referencing;
  const st = REF_STATUS[ref.status];
  const sent = !!ref.submittedAt;
  const open = ['form_sent', 'in_review', 'guarantor_needed', 'guarantor_in_review'].includes(ref.status);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    // The tab opens on the click (no pop-up block), then goes to Goodlord once the link is back.
    const tab = window.open('', '_blank');
    setBusy(true); setError(null);
    try {
      const { url } = await applicantApi.referencingLink(token);
      if (tab) { tab.opener = null; tab.location.href = url; } else window.location.href = url;
    } catch (e) { tab?.close(); setError(e); } finally { setBusy(false); }
  };
  const needed = v.documentsNeeded ?? ['id'];
  const has = (c: string) => v.documents.some((d) => d.category === c);
  const missing = needed.filter((c) => !has(c));
  return (
    <div className="space-y-5">
      <p className={`flex items-start gap-2 text-[14.5px] font-medium ${TONE[st.tone]}`}>
        {st.tone === 'ok' ? <Check className="mt-0.5 h-4 w-4 shrink-0" /> : <Clock className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>{st.text}</span>
      </p>
      {sent && open && !p.readOnly && (
        <div>
          <PillButton type="button" disabled={busy} onClick={() => void go()} className={ACTION}>
            {busy ? 'Opening…' : 'Continue your referencing'}
          </PillButton>
          <p className="mt-2 text-[13px] leading-relaxed text-ink-500">Goodlord has also emailed you the link{ref.submittedAt ? ` (sent ${formatDate(ref.submittedAt)})` : ''}.</p>
          <Err e={error} />
        </div>
      )}
      {ref.guarantor?.name && ref.status !== 'guarantor_needed' && <p className="text-[13px] text-ink-500">Guarantor: {ref.guarantor.name}</p>}
      {ref.status === 'guarantor_needed' && !p.readOnly && <GuarantorForm {...p} />}
      {missing.length > 0 ? (
        <div className="space-y-3">
          <p className="site-label pt-1">Your documents</p>
          {missing.includes('id') && <Uploads {...p} category="id" label="Passport *" hint={ID_HINT} />}
          {missing.includes('rtr') && <Uploads {...p} category="rtr" label="Right to rent document *" hint={RTR_HINT} />}
        </div>
      ) : (
        <p className="flex flex-wrap gap-x-4 gap-y-1 text-[13.5px] text-ink-600">
          {needed.map((c) => <span key={c} className="inline-flex items-center gap-1.5">{DOC_NAME[c] ?? c} on file<Check className="h-4 w-4 text-emerald-600" aria-label="on file" /></span>)}
        </p>
      )}
    </div>
  );
}

function GuarantorForm({ token, v, onDone }: PartProps) {
  const [name, setName] = useState(v.referencing.guarantor?.name ?? '');
  const [email, setEmail] = useState(v.referencing.guarantor?.email ?? '');
  const [phone, setPhone] = useState(v.referencing.guarantor?.phone ?? '');
  const ok = name.trim().split(/\s+/).length >= 2 && EMAIL_RE.test(email.trim()) && (!phone || phoneOk(phone));
  const m = useMutation({ mutationFn: () => applicantApi.guarantor(token, { name: name.trim(), email: email.trim(), phone: phone.trim() || null }), onSuccess: onDone });
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (ok) m.mutate(); }} className="space-y-4 rounded-xl border border-ink-200 bg-white p-4 sm:p-5">
      <p className="text-[15px] font-medium text-ink-900">Your guarantor</p>
      <div className="grid gap-4 sm:grid-cols-2 sm:gap-x-5">
        <F label="Full name *" className="sm:col-span-2"><input value={name} onChange={(e) => setName(e.target.value)} className="site-input" /></F>
        <F label="Email *"><input type="email" inputMode="email" autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} className="site-input" /></F>
        <F label="Phone" error={phone && !phoneOk(phone) ? phoneError(phone) : undefined}><PhoneInput kit="site" autoComplete="off" value={phone} onChange={setPhone} /></F>
      </div>
      <Err e={m.error} />
      <PillButton type="submit" small disabled={m.isPending || !ok} className={ACTION}>{m.isPending ? 'Sending…' : 'Send to Goodlord'}</PillButton>
    </form>
  );
}

function Sign(p: PartProps & { step: ApplicantStep }) {
  const { token, v, onDone } = p;
  const a = v.agreement;
  const [name, setName] = useState(v.applicant.name);
  const [agreed, setAgreed] = useState(false);
  const [drawn, setDrawn] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  const m = useMutation({ mutationFn: () => applicantApi.sign(token, { fullName: name, agreed, signatureDataUrl: typing ? null : drawn }), onSuccess: onDone });
  const fullName = name.trim().split(/\s+/).length >= 2;
  const ok = agreed && fullName && (typing || !!drawn);
  if (a.signedAt) {
    return (
      <div className="space-y-3 text-[14px]">
        <p className="flex items-start gap-2 text-emerald-700"><Check className="mt-0.5 h-4 w-4 shrink-0" />Signed by {a.signedBy} on {formatDate(a.signedAt)}.</p>
        {a.signed && <>
          <AgreementViewer key={a.signed.url} url={a.signed.url} />
          <a href={a.signed.url} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full border border-ink-200 bg-white px-5 text-[14px] font-medium text-ink-800 transition hover:border-ink-800 sm:w-auto"><FileText className="h-4 w-4 text-bronze-600" />Download the signed agreement</a>
        </>}
      </div>
    );
  }
  if (!a.toSign) return <p className="text-[14px] text-ink-500">{p.step.lockedBy}</p>;
  // The tenant signs in their box on the agreement's last page; the server stamps the same signature into the PDF.
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (ok) m.mutate(); }} className="space-y-4">
      <p className="text-[14px] leading-relaxed text-ink-600">Read it, then sign in the <b className="font-medium text-ink-900">Sign here</b> box on the last page.</p>
      <AgreementViewer key={a.toSign.url} url={a.toSign.url} overlay={<SignatureBox typed={typing ? name.trim() || null : null} onChange={setDrawn} />} />
      <button type="button" onClick={() => setTyping((t) => !t)} className="inline-flex min-h-[44px] items-center text-[14px] font-medium text-bronze-700 underline underline-offset-4 hover:text-ink-900">{typing ? 'Draw my signature instead' : 'Type my name instead'}</button>
      {(typing || !fullName) && <F label="Full name, as on your ID *"><input autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} className="site-input" /></F>}
      <label className="site-check !text-[14px]">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
        <span>I have read the tenancy agreement and the particulars at the front of it, and I agree to be bound by them. I understand this is my electronic signature. *</span>
      </label>
      <Err e={m.error} />
      <PillButton type="submit" small disabled={m.isPending || !ok} className={ACTION}>{m.isPending ? 'Signing…' : 'Sign the agreement'}</PillButton>
    </form>
  );
}

function Money({ token, v, onDone }: PartProps & { step: ApplicantStep }) {
  const m = useMutation({ mutationFn: () => applicantApi.declare(token, 'money'), onSuccess: onDone });
  const h = v.holdingDeposit;
  const total = v.money.rentMinor + v.money.depositMinor - (h.confirmed ? h.amountMinor : 0);
  return (
    <div className="space-y-4">
      <PayBox lines={[['First month\'s rent', v.money.rentMinor], [`Deposit (${v.money.depositWeeks === 6 ? 'six' : 'five'} weeks, protected)`, v.money.depositMinor], ...(h.confirmed ? [['Less holding deposit paid', -h.amountMinor] as [string, number]] : [])]} total={total} reference={`${v.flat} ${v.applicant.name.split(/\s+/).slice(-1)[0] ?? ''}`.trim()} currency={v.currency} />
      {v.money.declaredAt
        ? <p className="flex items-start gap-2 text-[14px] text-emerald-700"><Check className="mt-0.5 h-4 w-4 shrink-0" />Marked paid on {formatDate(v.money.declaredAt)} — we'll confirm when it lands.</p>
        : <><Err e={m.error} /><PillButton type="button" small disabled={m.isPending} onClick={() => m.mutate()} className={ACTION}>{m.isPending ? 'Saving…' : 'I\'ve paid the rent and deposit'}</PillButton></>}
    </div>
  );
}
